import { useCallback, useMemo, useReducer, useState, useTransition } from 'react';
import {
  CONFIGURATION_ACTION_TYPES,
  configurationReducer,
  createDefaultConfiguration,
  resolveSelectedOptions,
} from '../model/configuration';
import type { Configuration, SelectedOptions } from '../types';

/** 옵션 하나를 고르는 콜백. 어떤 종류인지는 어느 핸들러인지로 정해집니다. */
export type OptionSelectHandler = (optionId: string) => void;

export interface CarConfigurationController {
  configuration: Configuration;
  selectedOptions: SelectedOptions;
  /** 모델 GLB 전환 중에도 카드가 사용자가 방금 누른 값을 즉시 표시하기 위한 id입니다. */
  requestedModelId: string;
  onModelSelect: OptionSelectHandler;
  onStudioSelect: OptionSelectHandler;
  onWeatherSelect: OptionSelectHandler;
  onPaintSelect: OptionSelectHandler;
  onWheelSelect: OptionSelectHandler;
  onInteriorSelect: OptionSelectHandler;
}

export function useCarConfiguration(): CarConfigurationController {
  const [configuration, dispatch] = useReducer(configurationReducer, undefined, createDefaultConfiguration);

  // 차량 교체만 새 GLB를 받아야 하므로 transition으로 이전 장면을 유지합니다.
  const [isSwappingModel, startModelSwap] = useTransition();
  const [pendingModelId, setPendingModelId] = useState<string | null>(null);

  const onModelSelect = useCallback<OptionSelectHandler>((optionId) => {
    setPendingModelId(optionId);
    startModelSwap(() => {
      dispatch({ type: CONFIGURATION_ACTION_TYPES.SELECT_MODEL, optionId });
    });
  }, []);

  const onStudioSelect = useCallback<OptionSelectHandler>((optionId) => {
    dispatch({ type: CONFIGURATION_ACTION_TYPES.SELECT_STUDIO, optionId });
  }, []);

  const onWeatherSelect = useCallback<OptionSelectHandler>((optionId) => {
    dispatch({ type: CONFIGURATION_ACTION_TYPES.SELECT_WEATHER, optionId });
  }, []);

  const onPaintSelect = useCallback<OptionSelectHandler>((optionId) => {
    dispatch({ type: CONFIGURATION_ACTION_TYPES.SELECT_PAINT, optionId });
  }, []);

  const onWheelSelect = useCallback<OptionSelectHandler>((optionId) => {
    dispatch({ type: CONFIGURATION_ACTION_TYPES.SELECT_WHEEL, optionId });
  }, []);

  const onInteriorSelect = useCallback<OptionSelectHandler>((optionId) => {
    dispatch({ type: CONFIGURATION_ACTION_TYPES.SELECT_INTERIOR, optionId });
  }, []);

  const selectedOptions = useMemo(() => resolveSelectedOptions(configuration), [configuration]);
  const requestedModelId =
    isSwappingModel && pendingModelId !== null ? pendingModelId : selectedOptions.model.id;

  return {
    configuration,
    selectedOptions,
    requestedModelId,
    onModelSelect,
    onStudioSelect,
    onWeatherSelect,
    onPaintSelect,
    onWheelSelect,
    onInteriorSelect,
  };
}
